import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import {
  type BooleanSelectFilterOption,
  createBooleanFilter,
} from '../../filters/types/booleanFilter';
import type {
  TableColumnFormatLink,
  TableLinkColumnConfig,
} from '../../links/index';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { formatValueToCsvString } from './helpers/formatValueToCsvString';
import { mergeFieldDependencies } from './helpers/mergeFieldDependencies';

export const makeFormatBooleanTableCellValue =
  (options?: BooleanSelectFilterOption[]) =>
  (value: boolean | null): string => {
    const foundOption = options
      ? options.find((o) => o.value === `${value}`)
      : undefined;

    switch (value) {
      case true:
        return foundOption?.label ?? 'Yes';
      case false:
        return foundOption?.label ?? 'No';
      default:
        return foundOption?.label ?? '';
    }
  };

export type TableColumnFormatBoolean<TRow> = {
  type: 'boolean';
  field: KeyOfType<TRow, boolean | null | (boolean | null)[]>;
  options?: BooleanSelectFilterOption[];
} & TableColumnFormatLink<TRow>;

type BooleanColumnConfig<TRow, TDataType> = {
  options?: BooleanSelectFilterOption[];
} & TableDataColumnConfig<TRow, TDataType> &
  TableLinkColumnConfig<TRow>;

export const createBooleanColumn: {
  <TRow>(
    config: BooleanColumnConfig<TRow, boolean | null> & { array?: false },
  ): TableColumnBase<TRow, TableColumnFormatBoolean<TRow>>;
  <TRow>(
    config: BooleanColumnConfig<TRow, (boolean | null)[]> & { array: true },
  ): TableColumnBase<TRow, TableColumnFormatBoolean<TRow>>;
} = <TRow>(
  config: BooleanColumnConfig<TRow, boolean | null | (boolean | null)[]>,
): TableColumnBase<TRow, TableColumnFormatBoolean<TRow>> => ({
  ...pickTableColumnConfig(config),
  format: {
    type: 'boolean',
    field: config.key,
    createLinkProps: config.link?.createLinkProps,
    options: config.options,
  },
  csvStringDependencies: [config.key],
  webDependencies: mergeFieldDependencies(
    config.key,
    config.link?.dependencies,
  ),
  toCsvString: (row) =>
    formatValueToCsvString(
      getValueFromKeyOfType<TRow, boolean | null | (boolean | null)[]>(
        row,
        config.key,
      ),
      (v: boolean | null): string =>
        makeFormatBooleanTableCellValue(config.options)(v),
    ),
  filter: createDefaultFilter(
    config.filter,
    createBooleanFilter(config.options),
  ),
});
